import { Currencies, Currency, Money } from '@afterpay/types'

export const GIFT_CARD_PROMO_VALIDATION_STATUS_UNAVAILABLE = 'UNAVAILABLE'

export interface GiftCardPromoBreakdown {
  faceValue: Money
  discountAmount: Money
  displayName: string
  purchaseAmount: Money
  trackingProperties?: GiftCardPromoTrackingProperties
}

export interface GiftCardPromoTrackingProperties {
  offerPromoCode?: string
  giftCardOfferCampaignKey?: string
}

interface ParsedMoney {
  money: Money
  minorUnits: number
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value)

const isCurrency = (value: unknown): value is Currency => Object.values(Currencies).some((currency) => currency === value)

const parseMoney = (value: unknown): ParsedMoney | null => {
  if (!isRecord(value) || typeof value.amount !== 'string' || !isCurrency(value.currency)) {
    return null
  }

  const match = /^(\d{1,14})(?:\.(\d{1,2}))?$/.exec(value.amount)
  if (!match) {
    return null
  }

  const minorUnits = Number(`${match[1]}${(match[2] ?? '').padEnd(2, '0')}`)
  if (!Number.isSafeInteger(minorUnits) || minorUnits <= 0) {
    return null
  }

  return { money: { amount: value.amount, currency: value.currency }, minorUnits }
}

export const parseMoneyValue = (value: unknown): Money | null => parseMoney(value)?.money ?? null

export const isSameMoneyAmount = (left: unknown, right: unknown): boolean => {
  const first = parseMoney(left)
  const second = parseMoney(right)
  return first !== null && second !== null && first.money.currency === second.money.currency && first.minorUnits === second.minorUnits
}

export const isGiftCardPromoUnavailable = (custom: unknown): boolean =>
  isRecord(custom) && isRecord(custom.giftCardPromotion) && custom.giftCardPromotion.validationStatus === GIFT_CARD_PROMO_VALIDATION_STATUS_UNAVAILABLE

const MAX_PERCENT_OFF = 100

const trimmedString = (value: unknown): string | undefined => (typeof value === 'string' && value.trim() !== '' ? value.trim() : undefined)

export const getGiftCardPromoTrackingProperties = (custom: unknown): GiftCardPromoTrackingProperties => {
  if (!isRecord(custom)) {
    return {}
  }
  const promotion = isRecord(custom.giftCardPromotion) ? custom.giftCardPromotion : {}
  const offerPromoCode =
    trimmedString(custom.giftCardPromotionPromoCode) ?? (isGiftCardPromoUnavailable(custom) ? trimmedString(promotion.displayName) : undefined)
  const giftCardOfferCampaignKey = trimmedString(custom.giftCardPromotionCampaignKey)
  return {
    ...(offerPromoCode !== undefined && { offerPromoCode }),
    ...(giftCardOfferCampaignKey !== undefined && { giftCardOfferCampaignKey }),
  }
}

export const getGiftCardPromoPercentOff = (custom: unknown): number | null => {
  if (!isRecord(custom) || !isRecord(custom.giftCardPromotion)) {
    return null
  }
  const { percentOff } = custom.giftCardPromotion
  const value = typeof percentOff === 'string' ? Number(percentOff.trim()) : typeof percentOff === 'number' ? percentOff : Number.NaN
  return Number.isFinite(value) && value > 0 && value <= MAX_PERCENT_OFF ? value : null
}

export const parseGiftCardPromoBreakdown = (checkoutAmount: unknown, custom: unknown, displayedCurrency?: unknown): GiftCardPromoBreakdown | null => {
  if (!isRecord(custom) || !isRecord(custom.giftCardPromotion) || isGiftCardPromoUnavailable(custom)) {
    return null
  }

  const { displayName, discountAmount } = custom.giftCardPromotion
  if (typeof displayName !== 'string' || displayName.trim() === '') {
    return null
  }

  const purchase = parseMoney(checkoutAmount)
  const face = parseMoney(custom.giftCardAmount)
  const discount = parseMoney(discountAmount)
  if (!purchase || !face || !discount) {
    return null
  }

  const currency = purchase.money.currency
  if (
    face.money.currency !== currency ||
    discount.money.currency !== currency ||
    (displayedCurrency !== undefined && displayedCurrency !== currency) ||
    face.minorUnits - discount.minorUnits !== purchase.minorUnits
  ) {
    return null
  }

  return {
    faceValue: face.money,
    discountAmount: discount.money,
    displayName,
    purchaseAmount: purchase.money,
    trackingProperties: getGiftCardPromoTrackingProperties(custom),
  }
}
